import { useState, useEffect } from 'react';
import Button from '../../../components/button/button';
import Message from "../../../components/toast-message";
import Loader from "../../../components/form-loader/form-loader";
import PageContainer, { SettingsSection, SettingsActions } from "../components/settings-layout";
import { saveLLMSettings, validateLLMApiKey } from "../../../services/llm-settings";
import { useAppInfo } from "../../../helpers/hooks/common-hook";
import { classNames } from "../../../helpers/utils/css-utils";

import SelectInput from "../../../components/forms/select";
import TextBox from "../../../components/forms/text-box";

export const modelsByProvider = {
    openai: [
        // Reasoning models commented out from UI per requirements:
        // { name: "o1", value: "o1" },
        // { name: "o1-mini", value: "o1-mini" },
        // { name: "o3-mini", value: "o3-mini" },
        // { name: "gpt-4o", value: "gpt-4o" },
        // { name: "gpt-4o-mini", value: "gpt-4o-mini" },
        { name: "gpt-4.1", value: "gpt-4.1" },
        { name: "gpt-4.1-mini", value: "gpt-4.1-mini" },
        { name: "gpt-4.1-nano", value: "gpt-4.1-nano" },
        { name: "gpt-5", value: "gpt-5" },
        { name: "gpt-5-mini", value: "gpt-5-mini" },
        { name: "gpt-5.1", value: "gpt-5.1" },
        { name: "gpt-5.2", value: "gpt-5.2" },
        { name: "gpt-5.4", value: "gpt-5.4" },
        { name: "gpt-5.4-mini", value: "gpt-5.4-mini" },
        { name: "gpt-5.5", value: "gpt-5.5" },
        { name: "gpt-5.6-luna", value: "gpt-5.6-luna" },
        { name: "gpt-5.6-terra", value: "gpt-5.6-terra" },
        { name: "gpt-5.6-sol", value: "gpt-5.6-sol" },
        { name: "gpt-6-luna", value: "gpt-6-luna" },
        { name: "gpt-6-sol", value: "gpt-6-sol" },
        { name: "gpt-6.1-sol", value: "gpt-6.1-sol" },
        { name: "gpt-6-astra", value: "gpt-6-astra" },
    ],
    // claude: [
    //     { name: "claude-3-7-sonnet-20250219", value: "claude-3-7-sonnet-20250219" },
    //     { name: "claude-3-5-sonnet-20241022", value: "claude-3-5-sonnet-20241022" },
    //     { name: "claude-3-5-haiku-20241022", value: "claude-3-5-haiku-20241022" },
    //     { name: "claude-3-opus-20240229", value: "claude-3-opus-20240229" },
    // ],
    gemini: [
        { name: "gemini-2.5-flash", value: "gemini-2.5-flash" },
        { name: "gemini-2.5-pro", value: "gemini-2.5-pro" },
        { name: "gemini-2.5-flash-lite", value: "gemini-2.5-flash-lite" },
        { name: "gemini-2.0-flash", value: "gemini-2.0-flash" },
        { name: "gemini-2.0-flash-lite", value: "gemini-2.0-flash-lite" },
        { name: "gemini-3-flash-preview", value: "gemini-3-flash-preview" },
        { name: "gemini-3-pro-preview", value: "gemini-3-pro-preview" },
    ]
}

function Badge({ text, indicate }) {
    const svgClass = classNames(indicate === null ? "fill-gray-400" : indicate ? "fill-green-500" : "fill-red-500", "h-1.5 w-1.5")
    return (
        <span
            className="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium text-gray-900 ring-1 ring-inset ring-gray-200">
            <svg className={svgClass} viewBox="0 0 6 6" aria-hidden="true">
                <circle cx={3} cy={3} r={3} />
            </svg>
            {text}
        </span>
    )
}

export default function LLMSettings({ navigationPosition = "below-header" }) {
    const { appId } = useAppInfo();
    const [formData, setFormData] = useState({
        provider: 'openai',
        apiKey: '',
        model: ''
        // model: 'gpt-4.1'
    });

    const [validateStatus, setValidateStatus] = useState({
        status: null,
    });

    const [error, setError] = useState({
        apiKey: false
    });

    const [formState, setFormState] = useState({
        processing: false,
        success: false,
        failed: false,
        message: ''
    });

    const handleChange = event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;

        const newFormData = { ...formData };
        newFormData[targetName] = targetValue;
        if (targetName === "provider") newFormData.model = modelsByProvider[targetValue]?.[0]?.value || "";
        if (targetName === "provider" || targetName === "apiKey") setValidateStatus({ status: null });

        setError({ [targetName]: false });
        setFormData(newFormData);
    };

    const validateForm = () => {
        let valid = true;

        // if (formData.apiKey === '') {
        //     error.apiKey = true;
        //     valid = false;
        // } else {
        //     error.apiKey = false;
        // }

        setError(Object.assign({}, error))
        return valid;
    }

    const updateFormState = (processing, success, failed, message) => {
        setFormState(Object.assign({}, { processing, success, failed, message }))
    }

    const handleOnSubmit = async (event) => {
        event.preventDefault();
        if (!formState.processing && validateForm()) {
            updateFormState(true, false, false);
            const { response, status } = await saveLLMSettings(appId, formData);
            if (status === 200) {
                updateFormState(false, true, false, "Successfully saved");
            } else {
                updateFormState(false, false, true, response);
            }
        }
    };

    const handleValidateApiKey = async () => {
        updateFormState(true, false, false);
        const { response, status } = await validateLLMApiKey(appId, formData.apiKey);
        if (status === 200) {
            updateFormState(false, true, false, "Validation completed");
            setValidateStatus(response)
            setFormData({ ...formData, provider: response.llm?.provider || formData.provider, model: response.llm?.chatModel || formData.model });
        } else {
            updateFormState(false, false, true, response);
        }
    };

    useEffect(() => {
        handleValidateApiKey();
    }, []);


    return (
        <PageContainer
            navigationPosition={navigationPosition}
            title="LLM Settings"
            subtitle="Choose the model that powers your agent’s conversations.">

            <form className="flex max-w-4xl flex-col gap-6" onSubmit={handleOnSubmit}>

                {formState.processing && <div className="p-4"><Loader /></div>}
                {formState.success && <Message text={formState.message} type="success" />}
                {formState.failed && <Message text={formState.message} type="error" />}

                <fieldset disabled={formState.processing} className="min-w-0 space-y-6 disabled:opacity-60">
                    <SettingsSection title="Model configuration" description="Select a provider and the model your agent will use.">
                        <div className="grid gap-5 sm:grid-cols-2">
                            <SelectInput
                                rootClass="w-full"
                                label="LLM Provider*"
                                id="llm_provider"
                                name="provider"
                                value={formData.provider}
                                onChange={handleChange}
                                optionsComp={<>
                                    <option value="openai">OpenAI</option>
                                    {/* <option value="claude">Claude</option> */}
                                    <option value="gemini">Gemini</option>
                                </>}
                            />

                            {/* <SelectInput
                    rootClass="w-full"
                    label="Model*"
                    id="llm_model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    optionsComp={<>
                        {models.map(model => (
                            <option key={model.value} value={model.value}>{model.name}</option>
                        ))}
                    </>}
                /> */}

                        </div>
                    </SettingsSection>
                    <SettingsSection title="Provider credentials" description="Supply your own API key and validate the connection before saving.">
                        <TextBox
                            rootClass="w-full"
                            label="API Key"
                            type="password"
                            name="apiKey"
                            id="apiKey"
                            value={formData.apiKey}
                            placeholder="Enter Your Own API Key"
                            onChange={handleChange}
                            error={error.apiKey}
                        />

                        <div className="flex flex-wrap items-center gap-3">
                            <Badge text={validateStatus.status ? "Valid API Key Provided" : validateStatus.status === null ? "Not validated" : "Invalid API key"} indicate={validateStatus.status} />
                        </div>

                    </SettingsSection>
                    <SettingsActions>
                        <Button color="default" variant="button" type="button" disabled={formState.processing} onClick={handleValidateApiKey} text="Validate key" />
                        <Button variant="button" type="submit" disabled={formState.processing} loading={formState.processing} text="Save changes" />
                    </SettingsActions>
                </fieldset>
            </form>

        </PageContainer>
    )
}
